import {
  Divider,
  Grid,
  Group,
  NumberInput,
  Paper,
  Select,
  SimpleGrid,
  Stack,
  Text
} from '@mantine/core';
import {
  IconBuildingBank,
  IconCash,
  IconCheck,
  IconCreditCard,
  IconDeviceMobileDollar
} from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { ICashierBox } from '../../interfaces/Store';
import { formatMoneyString } from '../../utils/strings';
import AmountToApplyLabel from './AmountToApplyLabel';

export type PaymentCaptureMethodOption = {
  value: string;
  label: string;
  Icon?: any;
};

const defaultPaymentMethodIcons: Record<string, any> = {
  card: IconCreditCard,
  cash: IconCash,
  transfer: IconBuildingBank,
  mercadopago: IconDeviceMobileDollar
};

export default function PaymentCaptureFields({
  cashierboxes,
  cashierBoxInputProps,
  paymentMethod,
  onPaymentMethodChange,
  paymentMethodOptions,
  paymentMethodTestIdPrefix,
  amountInputProps,
  amountInputTestId,
  amountReadOnly = false,
  amountDisabled = false,
  amountDue,
  amountToApply,
  remainingBalance,
  showCashReceived,
  cashReceivedAmount = '',
  onCashReceivedAmountChange,
  cashReceivedInputTestId,
  changeAmount
}: {
  cashierboxes: ICashierBox[] | undefined;
  cashierBoxInputProps: any;
  paymentMethod: string;
  onPaymentMethodChange: (value: string) => void;
  paymentMethodOptions: PaymentCaptureMethodOption[];
  paymentMethodTestIdPrefix: string;
  amountInputProps: any;
  amountInputTestId?: string;
  amountReadOnly?: boolean;
  amountDisabled?: boolean;
  amountDue: number;
  amountToApply: number;
  remainingBalance: number;
  showCashReceived: boolean;
  cashReceivedAmount?: number | '';
  onCashReceivedAmountChange?: (value: number | '') => void;
  cashReceivedInputTestId?: string;
  changeAmount: number;
}) {
  const { t } = useTranslation(['FieldName']);

  return (
    <>
      <Grid.Col>
        <Select
          allowDeselect={false}
          label={t('FieldName:cashierBox')}
          placeholder={t('FieldName:cashierBox')}
          defaultValue={(cashierboxes || [])[0]?._id || ''}
          data={(cashierboxes || []).map((cashierbox) => {
            return {
              label: cashierbox.name,
              value: cashierbox._id || '',
              key: cashierbox._id || cashierbox.name
            };
          })}
          {...cashierBoxInputProps}
        />
      </Grid.Col>
      <Grid.Col>
        <Stack gap="xs">
          <Text size="sm" fw={500}>
            {t('FieldName:paymentMethod')}
          </Text>
          <SimpleGrid
            cols={{ base: 1, sm: Math.min(paymentMethodOptions.length || 1, 4) }}
            spacing="sm"
          >
            {paymentMethodOptions.map(({ value, label, Icon }) => {
              const isSelected = paymentMethod === value;
              const PaymentIcon = Icon || defaultPaymentMethodIcons[value] || IconCreditCard;

              return (
                <Paper
                  key={value}
                  component="button"
                  type="button"
                  data-testid={`${paymentMethodTestIdPrefix}-${value}`}
                  withBorder
                  radius="md"
                  p="md"
                  bg={isSelected ? 'blue.0' : 'white'}
                  style={{
                    borderColor: isSelected ? 'var(--mantine-color-blue-6)' : undefined,
                    cursor: 'pointer',
                    position: 'relative',
                    minHeight: 96
                  }}
                  onClick={() => onPaymentMethodChange(value)}
                >
                  {isSelected && (
                    <IconCheck
                      size={20}
                      style={{
                        position: 'absolute',
                        top: 8,
                        right: 8,
                        color: 'var(--mantine-color-blue-7)'
                      }}
                    />
                  )}
                  <Stack gap="xs" align="center">
                    <PaymentIcon size={30} />
                    <Text size="sm" ta="center">
                      {label}
                    </Text>
                  </Stack>
                </Paper>
              );
            })}
          </SimpleGrid>
        </Stack>
      </Grid.Col>
      <Grid.Col>
        <NumberInput
          data-testid={amountInputTestId}
          placeholder={t('FieldName:amountToApply')}
          allowLeadingZeros={false}
          allowNegative={false}
          decimalScale={2}
          min={0}
          label={<AmountToApplyLabel />}
          readOnly={amountReadOnly}
          disabled={amountDisabled}
          {...amountInputProps}
        />
      </Grid.Col>
      {showCashReceived && (
        <Grid.Col>
          <NumberInput
            data-testid={cashReceivedInputTestId}
            placeholder={t('FieldName:cashReceived')}
            allowLeadingZeros={false}
            allowNegative={false}
            decimalScale={2}
            min={0}
            label={t('FieldName:cashReceived')}
            value={cashReceivedAmount}
            onChange={(value) =>
              onCashReceivedAmountChange?.(value === '' ? '' : Number(value || 0))
            }
          />
        </Grid.Col>
      )}
      <Grid.Col>
        <Paper withBorder radius="md" p="md" bg="gray.0">
          <Stack gap="xs">
            <Text fw={700}>{t('FieldName:paymentSummary')}</Text>
            <Group justify="space-between">
              <Text>{t('FieldName:amountDue')}:</Text>
              <Text fw={600}>{formatMoneyString(amountDue)}</Text>
            </Group>
            <Group justify="space-between">
              <Text>{t('FieldName:amountToApply')}:</Text>
              <Text fw={600}>{formatMoneyString(amountToApply)}</Text>
            </Group>
            <Group justify="space-between">
              <Text>{t('FieldName:remainingBalance')}:</Text>
              <Text fw={600}>{formatMoneyString(remainingBalance)}</Text>
            </Group>
            {showCashReceived && (
              <>
                <Group justify="space-between">
                  <Text>{t('FieldName:cashReceived')}:</Text>
                  <Text fw={600}>{formatMoneyString(Number(cashReceivedAmount || 0))}</Text>
                </Group>
                <Divider />
                <Group justify="space-between">
                  <Text>{t('FieldName:changeToReturn')}:</Text>
                  <Text fw={700} c="green.8">
                    {formatMoneyString(changeAmount)}
                  </Text>
                </Group>
              </>
            )}
          </Stack>
        </Paper>
      </Grid.Col>
    </>
  );
}
